(ns common.permissions.account-permissions
  (:require
   [common.data-types :as common-data-types]
   [common.permissions.field-permissions :as field-permissions]
   [common.permissions.entity-permissions :as entity-permissions]
   [common.roles :as roles]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.field-spec :as field-spec]
   [common.schema.registry :as registry]))

(def ^:private zadmin-force-create-fields
  "Legacy zadmin carve-out: these dashboard fields are always full-access for zadmin."
  #{:metabase-dashboard/label
    :metabase-dashboard/metabase-dashboard-id
    :metabase-dashboard/pipeline-filtering?})

(defn- resolve-field-context
  "Resolve requested/effective field metadata once so permission logic can use a single context.

  Currency symbol fields are permissioned via their related currency attribute.
  This applies consistently across all roles."
  [tenant-schema requested-field-attribute]
  (let [requested-field-schema (registry/get-field-schema tenant-schema requested-field-attribute)
        effective-field-attribute (if (= :data.type/currency-symbol (:field/data-type requested-field-schema))
                                    (or (:field/related-fields requested-field-schema)
                                        requested-field-attribute)
                                    requested-field-attribute)
        effective-field-schema (if (= effective-field-attribute requested-field-attribute)
                                 requested-field-schema
                                 (registry/get-field-schema tenant-schema effective-field-attribute))]
    {:requested-field-attribute requested-field-attribute
     :effective-field-attribute effective-field-attribute
     :effective-field-schema effective-field-schema}))

(defn- resolve-field-in-use
  "Resolve the effective in-use setting for a field using settings UI precedence:
  mandatory always -> tenant override -> schema default visibility.
  Returns a keyword such as :in-use, :in-use-disabled, :not-in-use, etc."
  [tenant entity-type field-attribute field-schema]
  (let [tenant-field-configurations (some-> tenant
                                            :tenant/field-configurations
                                            common-data-types/parse-edn-string)]
    (or (when (field-spec/system-required-or-legacy-always-mandatory-field? field-schema)
          :in-use-disabled)
        (get-in tenant-field-configurations
                [entity-type
                 field-attribute
                 :field-settings/in-use-config])
        (get-in field-schema [:field/default-visibility :field-visibility]))))

(defn- field-in-use?
  "Returns true when a field is configured as in-use or in-use-disabled for the tenant."
  [tenant entity-type field-attribute field-schema]
  (contains? field-permissions/POSITIVE-IN-USE-VALUES
             (resolve-field-in-use tenant entity-type field-attribute field-schema)))

(defn- zadmin-access-level
  "Resolve zadmin field access with in-use gating.

  If a field is not in-use for the tenant, zadmin gets :none.
  Otherwise, access is derived from default authorization, with explicit zadmin carve-outs."
  [tenant entity-type requested-field-attribute effective-attribute effective-field-schema]
  (if-not (field-in-use? tenant entity-type effective-attribute effective-field-schema)
    :none
    (if (contains? zadmin-force-create-fields requested-field-attribute)
      :x
      (roles/resolve-role-field-access :role/zadmin
                                       (:field/default-authorization effective-field-schema)))))

(declare access-to-field)

(defn- composite-attrs-accessible?
  "Returns true when every composite source attribute of a calculated field
  has at least read access for this user. An empty list passes."
  [tenant tenant-schema account role account-current-permissions entity-type field-schema]
  (every? (fn [attr]
            (let [a (access-to-field tenant tenant-schema account role
                                     account-current-permissions attr entity-type nil)]
              (and (some? a) (not= a :none))))
          (or (:field/composite-attributes field-schema) [])))

(defn- pipeline-or-global-access
  "Resolve configured field access from account permission maps.

  For pipeline entities, prefer pipeline-scoped access and fall back to global.
  For non-pipeline entities, use global access only."
  [account-current-permissions pipeline-id entity-type field-attribute]
  (let [pipeline-access (when pipeline-id
                          (-> account-current-permissions
                              :account-pipeline-field-access
                              (get pipeline-id)
                              (get entity-type)
                              (get field-attribute)))
        global-access (-> account-current-permissions
                          :account-global-field-access
                          (get entity-type)
                          (get field-attribute))]
    (if pipeline-id
      (or pipeline-access global-access)
      global-access)))

(defn account-module-access?
  "Whether this account may read entities gated behind `module`.

  Deliberately mirrors two existing gates that must agree with each other: the
  `:gate/modules` facet an entity type declares in
  `common.opensearch.read-gates`, from which
  `backend.search.entity-indices/get-permissions-fn-maker` derives the
  dedicated index's `permissions.accounts`, and the entity type's own
  `:entity/read-permissions-check-fn` in `common.schema.entity-types` (e.g.
  :entity.type/investor's, which is exactly a `:module/fundraising` membership
  test). Admins bypass both - the index because
  `backend.domain.search.permissions/permissions-queries` only adds the
  `permissions.accounts` clause for non-admins, the entity check because
  `read-permissions?` short-circuits for admin unless the type sets
  `:entity/apply-read-permissions-to-admin?`.

  Note this does NOT also require the module on `:tenant/modules`, unlike
  `common.schema.view.single-entity/module-access?`. Neither of the two gates
  above consults it, and being stricter here than the index actually is would
  hide fields the index would serve. Keep those three in step.

  `account-current-permissions` is expected already parsed; a raw EDN string
  yields nil modules and therefore denies, which is the safe direction."
  [role account-current-permissions module]
  (boolean
   (or (roles/admin? role)
       (contains? (set (:account-modules account-current-permissions)) module))))

(defn access-to-field
  [tenant tenant-schema account role account-current-permissions field-attribute entity-type optional-pipeline]
  (when-not (some #{field-attribute} (:tenant/blacklisted-fields tenant))
    (let [{:keys [requested-field-attribute effective-field-attribute effective-field-schema]}
          (resolve-field-context tenant-schema field-attribute)
          field-attribute effective-field-attribute
          field-schema    effective-field-schema
          pipeline-id     (when (pipeline/pipeline-entity? entity-type)
                            (or (:db/id optional-pipeline)
                                optional-pipeline))
          calculated?     (and (:field/calculated-field field-schema)
                               (not (:field/aggregate-attribute field-schema)))
          access          (cond
                            (or (not (some #{entity-type} entity-permissions/CONFIGURABLE_ENTITIES))
                                (:field/hidden-from-configuration? field-schema))
                            (roles/resolve-role-field-access role (:field/default-authorization field-schema))

                            :else
                            (cond
                              (roles/sys-admin? role)
                              :x
                              (roles/zadmin? role)
                              (zadmin-access-level tenant
                                                   entity-type
                                                   requested-field-attribute
                                                   field-attribute
                                                   field-schema)
                              ;; note: admins are not exempt from pipeline field config —
                              ;; resolve-pipelines-config already returns :none for a field the
                              ;; pipeline disabled, before the per-role branch is reached:
                              (roles/admin? role)
                              (pipeline-or-global-access account-current-permissions
                                                         pipeline-id
                                                         entity-type
                                                         field-attribute)
                              :else
                              (pipeline-or-global-access account-current-permissions
                                                         pipeline-id
                                                         entity-type
                                                         field-attribute)))]
      ;; Calculated field post-processing (aggregate-backed fields are exempt — they are
      ;; editable views and go through normal access rules):
      ;;  1. field must be configured as in-use for this tenant
      ;;  2. the configured access resolved above must not deny the field. `access`
      ;;     is pipeline-scoped for pipeline entities, so this is what stops a
      ;;     calculated field the pipeline turned off from leaking back in. The
      ;;     tenant-level check in 1. cannot see :pipeline/settings at all.
      ;;  3. every composite source attribute must be readable by this user
      ;;  4. max permission is :r
      (if calculated?
        (when (and (field-in-use? tenant entity-type field-attribute field-schema)
                   (some? access)
                   (not= :none access)
                   #_(composite-attrs-accessible? tenant tenant-schema account role
                                                  account-current-permissions entity-type field-schema))
          :r)
        access))))

(defn field-read-access?
  [tenant tenant-schema account role account-current-permissions field-attribute entity-type
   optional-pipeline]
  (let [access-type (access-to-field tenant
                                     tenant-schema
                                     account
                                     role
                                     account-current-permissions
                                     field-attribute
                                     entity-type
                                     optional-pipeline)
        res (case access-type
              :x true
              :w true
              :r true
              false)]
    res))

(defn field-write-access?
  [tenant tenant-schema account role account-current-permissions field-attribute entity-type
   optional-pipeline]
  (let [res (case (access-to-field tenant
                                   tenant-schema
                                   account
                                   role
                                   account-current-permissions
                                   field-attribute
                                   entity-type
                                   optional-pipeline)
              :x true
              :w true
              false)]
    res))

(defn field-option-create-access?
  [tenant tenant-schema account role account-current-permissions field-attribute entity-type
   optional-pipeline]
  (let [res (case (access-to-field tenant
                                   tenant-schema
                                   account
                                   role
                                   account-current-permissions
                                   field-attribute
                                   entity-type
                                   optional-pipeline)
              :x true
              false)]
    res))
